import { useQuery } from '@tanstack/react-query';

import { AdminQueryKeys, getAdminTenantRoi, type AdminTenantRoiResponse } from '../api';

/**
 * ADMIN-ONLY per-tenant ROI + cost panel (Phase C4). Shows the value delivered to the tenant alongside
 * DocuSense's real cost (COGS), the margin, a value-to-price churn ratio and a per-model breakdown —
 * including model names and costs that are deliberately hidden from the tenant-facing ROI view.
 */
export function TenantRoiCostSection({ tenantId }: { tenantId: string }): JSX.Element {
  const { data, isLoading, isError } = useQuery({
    queryKey: AdminQueryKeys.tenantRoi(tenantId),
    queryFn: ({ signal }) => getAdminTenantRoi(tenantId, signal),
    enabled: tenantId !== '',
  });

  return (
    <section className="card p-5">
      <h2 className="text-sm font-semibold text-slate-900">ROI y consumo</h2>
      <p className="mt-1 text-xs text-slate-500">
        Valor entregado al cliente frente al coste real (COGS) y el margen. Solo visible para administradores.
      </p>

      {isLoading ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-slate-100" />
          ))}
        </div>
      ) : isError || data === undefined ? (
        <div className="mt-4 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          No se pudo cargar el ROI/consumo de este tenant.
        </div>
      ) : (
        <Body data={data} />
      )}
    </section>
  );
}

function Body({ data }: { data: AdminTenantRoiResponse }): JSX.Element {
  return (
    <div className="mt-4 space-y-4">
      {data.churnRisk && data.valueToPriceRatio !== null ? (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          ⚠ Riesgo de margen/churn: el valor entregado es {formatRatio(data.valueToPriceRatio)} la suscripción
          (por debajo de 2×). Revisar la cuenta antes de la renovación.
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label="Valor entregado"
          value={formatMoney(data.savingsAmount, data.currency)}
          help={`${data.hoursSaved.toLocaleString('es-ES', { maximumFractionDigits: 1 })} h · ${formatPercent(data.automationRate)} auto`}
        />
        <Kpi
          label="Coste IA (COGS)"
          value={formatMoney(data.aiCost, data.aiCostCurrency)}
          help={`${data.documentsProcessed.toLocaleString('es-ES')} documentos`}
        />
        <Kpi
          label="Margen"
          value={data.margin === null ? '—' : formatMoney(data.margin, data.currency)}
          help={
            data.subscriptionMonthlyCost === null
              ? 'Plan sin coste'
              : data.margin === null
                ? 'Monedas distintas'
                : `Suscripción ${formatMoney(data.subscriptionMonthlyCost, data.currency)}`
          }
        />
        <Kpi
          label="Valor / precio"
          value={data.valueToPriceRatio === null ? '—' : formatRatio(data.valueToPriceRatio)}
          help={data.churnRisk ? 'Riesgo de churn' : 'Saludable'}
          tone={data.churnRisk ? 'warn' : 'ok'}
        />
      </div>

      <ModelTable rows={data.modelBreakdown} />
    </div>
  );
}

interface KpiProps {
  label: string;
  value: string;
  help: string;
  tone?: 'ok' | 'warn' | undefined;
}

function Kpi({ label, value, help, tone }: KpiProps): JSX.Element {
  const valueColor = tone === 'warn' ? 'text-amber-700' : 'text-slate-900';
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${valueColor}`}>{value}</p>
      <p className="mt-1 text-xs text-slate-500">{help}</p>
    </div>
  );
}

function ModelTable({ rows }: { rows: AdminTenantRoiResponse['modelBreakdown'] }): JSX.Element {
  if (rows.length === 0) {
    return (
      <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-500">
        Sin consumo de IA en el periodo.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="py-2 pr-4 font-medium">Modelo</th>
            <th className="py-2 pr-4 text-right font-medium">Llamadas</th>
            <th className="py-2 pr-4 text-right font-medium">Coste</th>
            <th className="py-2 pr-4 text-right font-medium">Latencia media</th>
            <th className="py-2 text-right font-medium">Éxito</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.modelName} className="border-b border-slate-100 last:border-0">
              <td className="py-2 pr-4 font-mono text-xs text-slate-700">{row.modelName}</td>
              <td className="py-2 pr-4 text-right tabular-nums text-slate-700">{row.callCount}</td>
              <td className="py-2 pr-4 text-right tabular-nums text-slate-700">{formatMoney(row.cost, row.currency)}</td>
              <td className="py-2 pr-4 text-right tabular-nums text-slate-700">{(row.averageLatencyMs / 1000).toFixed(1)} s</td>
              <td className="py-2 text-right tabular-nums text-slate-700">{formatPercent(row.successRate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- formatters ----------

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${amount.toLocaleString('es-ES', { maximumFractionDigits: 2 })} ${currency}`;
  }
}

function formatPercent(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}

function formatRatio(ratio: number): string {
  return `${ratio.toLocaleString('es-ES', { maximumFractionDigits: 1 })}×`;
}
